"use client";

import { useEffect, useState } from "react";
import { Button, type ButtonVariant } from "./button";

type ButtonSize = "default" | "sm" | "lg" | "icon" | "icon-sm";

type Props = {
  /** Called when confirmed (second click). Can be async. */
  onConfirm: () => unknown;
  /** Text/content shown in armed state. Default: "Confirm?" */
  confirmContent?: React.ReactNode;
  /** Initial button content */
  children: React.ReactNode;
  /** Button variant. Changes to destructive when armed. */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Disabled state */
  disabled?: boolean;
  /** Auto-reset timeout in ms. Default: 3000. Set to 0 to disable. */
  timeout?: number;
  /** Additional className */
  className?: string;
  /** Accessible label for the button */
  "aria-label"?: string;
};

/**
 * Button that requires two clicks to execute a destructive action.
 * First click arms the button (shows confirm state), second click executes.
 * Auto-resets after timeout.
 */
export function ConfirmingButton({
  onConfirm,
  confirmContent = "Confirm?",
  children,
  variant = "destructive-outline",
  size = "default",
  disabled = false,
  timeout = 3000,
  className,
  "aria-label": ariaLabel,
}: Props) {
  const [isArmed, setIsArmed] = useState(false);
  const [isPending, setIsPending] = useState(false);

  // Auto-reset after timeout
  useEffect(() => {
    if (!isArmed || timeout === 0) return;
    const timer = setTimeout(() => setIsArmed(false), timeout);
    return () => clearTimeout(timer);
  }, [isArmed, timeout]);

  const handleClick = async () => {
    if (!isArmed) {
      setIsArmed(true);
      return;
    }

    setIsPending(true);
    try {
      await onConfirm();
    } finally {
      setIsPending(false);
      setIsArmed(false);
    }
  };

  return (
    <Button
      variant={isArmed ? "destructive" : variant}
      size={size}
      onClick={handleClick}
      disabled={disabled || isPending}
      className={className}
      aria-label={ariaLabel}
      aria-pressed={isArmed}
    >
      {isArmed ? confirmContent : children}
      {/* Screen reader announcement when armed */}
      <span className="sr-only" aria-live="assertive">
        {isArmed ? "Click again to confirm" : ""}
      </span>
    </Button>
  );
}
